'use client';

import React, { useState, useMemo, Suspense, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchColleges, College } from '@/lib/services/collegeService';
import { MapPin, Search, Star, Building2, ChevronRight, Filter } from 'lucide-react';
import Link from 'next/link';

const allCities = [
  { name: 'Delhi', state: 'Delhi', icon: '🏛️' },
  { name: 'Mumbai', state: 'Maharashtra', icon: '🌆' },
  { name: 'Bengaluru', state: 'Karnataka', icon: '🌿' },
  { name: 'Chennai', state: 'Tamil Nadu', icon: '🏖️' },
  { name: 'Hyderabad', state: 'Telangana', icon: '💎' },
  { name: 'Pune', state: 'Maharashtra', icon: '🎓' },
  { name: 'Kolkata', state: 'West Bengal', icon: '🌉' },
  { name: 'Ahmedabad', state: 'Gujarat', icon: '🏗️' },
  { name: 'Jaipur', state: 'Rajasthan', icon: '🏰' },
  { name: 'Lucknow', state: 'Uttar Pradesh', icon: '🕌' },
  { name: 'Noida', state: 'Uttar Pradesh', icon: '🏢' },
  { name: 'Chandigarh', state: 'Punjab', icon: '🌳' },
  { name: 'Mohali', state: 'Punjab', icon: '🏙️' },
  { name: 'Pilani', state: 'Rajasthan', icon: '🏫' },
  { name: 'Manipal', state: 'Karnataka', icon: '🌊' },
  { name: 'Vellore', state: 'Tamil Nadu', icon: '🏯' },
  { name: 'Tiruchirappalli', state: 'Tamil Nadu', icon: '🌺' },
  { name: 'New Delhi', state: 'Delhi', icon: '🏛️' },
];

const allStates = ['All States', 'Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Telangana', 'Punjab', 'West Bengal', 'Gujarat', 'Rajasthan', 'Uttar Pradesh'];

function CitySearchContent() {
  const searchParams = useSearchParams();
  const initialCity = searchParams?.get('city') || '';

  const [cityQuery, setCityQuery] = useState(initialCity);
  const [selectedState, setSelectedState] = useState('All States');
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [colleges, setColleges] = useState<College[]>([]);

  useEffect(() => {
    fetchColleges().then(setColleges);
  }, []);

  const filteredCities = useMemo(() => {
    return allCities.filter((c) => {
      const matchesQuery = c.name.toLowerCase().includes(cityQuery.toLowerCase());
      const matchesState = selectedState === 'All States' || c.state === selectedState;
      return matchesQuery && matchesState;
    });
  }, [cityQuery, selectedState]);

  const cityColleges = useMemo(() => {
    if (!selectedCity) return [];
    return colleges.filter((c) =>
      c.location.toLowerCase().includes(selectedCity.toLowerCase()) ||
      c.state.toLowerCase().includes(selectedCity.toLowerCase())
    );
  }, [selectedCity]);

  const formatFees = (amount: number) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <div className="bg-secondary text-white py-10 px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-3">
              <Link href="/home" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>City-wise College Search</span>
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">Find Colleges by City</h1>
            <p className="text-slate-300 text-sm max-w-xl">
              Discover top colleges in your preferred city across India. Filter by state, stream, and more.
            </p>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: City selector */}
            <div className="lg:w-80 shrink-0">
              <div className="bg-card rounded-2xl border border-border p-5 sticky top-24">
                <h2 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <MapPin size={16} className="text-primary" />
                  Select a City
                </h2>

                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                  <input
                    type="text"
                    placeholder="Search city..."
                    value={cityQuery}
                    onChange={(e) => setCityQuery(e.target.value)}
                    className="input-base pl-9 text-sm"
                  />
                </div>

                {/* State filter */}
                <div className="relative mb-4">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="select-base pl-9 text-sm appearance-none cursor-pointer"
                  >
                    {allStates.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* City list */}
                <div className="space-y-1 max-h-96 overflow-y-auto scrollbar-thin">
                  {filteredCities.map((city) => (
                    <button
                      key={city.name}
                      onClick={() => setSelectedCity(city.name)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                        selectedCity === city.name
                          ? 'bg-primary text-white' :'hover:bg-muted text-foreground'
                      }`}
                    >
                      <span className="text-lg">{city.icon}</span>
                      <div>
                        <p className="font-semibold text-sm">{city.name}</p>
                        <p className={`text-xs ${selectedCity === city.name ? 'text-white/70' : 'text-muted-foreground'}`}>{city.state}</p>
                      </div>
                    </button>
                  ))}
                  {filteredCities.length === 0 && (
                    <p className="text-center text-muted-foreground text-sm py-6">No cities found</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right: College results */}
            <div className="flex-1 min-w-0">
              {selectedCity ? (
                <>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-xl font-bold text-foreground">
                        Colleges in {selectedCity}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {cityColleges.length} college{cityColleges.length !== 1 ? 's' : ''} found
                      </p>
                    </div>
                  </div>

                  {cityColleges.length > 0 ? (
                    <div className="space-y-4">
                      {cityColleges.map((college) => (
                        <div key={college.id} className="bg-card rounded-2xl border border-border p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                          <div className="flex gap-4">
                            <div className="relative w-24 h-20 rounded-xl overflow-hidden shrink-0">
                              <img
                                src={college.image}
                                alt={`${college.name} campus`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    {college.nirfRank && (
                                      <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-full">NIRF #{college.nirfRank}</span>
                                    )}
                                    <span className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded-full">{college.accreditation}</span>
                                  </div>
                                  <h3 className="font-bold text-foreground text-base line-clamp-1">{college.name}</h3>
                                  <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
                                    <MapPin size={11} />
                                    <span className="text-xs">{college.location}, {college.state}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <Star size={13} className="text-amber-400 fill-amber-400" />
                                  <span className="font-bold text-sm text-foreground">{college.rating}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3 mt-3">
                                <div className="bg-muted rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-muted-foreground">Fees</p>
                                  <p className="font-bold text-sm text-foreground font-tabular">{formatFees(college.totalFees)}</p>
                                </div>
                                <div className="bg-muted rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-muted-foreground">Cutoff</p>
                                  <p className="font-bold text-sm text-primary font-tabular">{college.cutoffScore}</p>
                                </div>
                                <div className="bg-muted rounded-lg px-3 py-2 text-center">
                                  <p className="text-xs text-muted-foreground">Ownership</p>
                                  <p className="font-bold text-xs text-foreground">{college.ownership}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-3">
                                <Link href="/" className="btn-secondary text-xs py-1.5 px-4">View Details</Link>
                                <Link href="/college-predictor" className="btn-primary text-xs py-1.5 px-4">Check Chances</Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-card rounded-2xl border border-border">
                      <Building2 size={48} className="text-muted-foreground mx-auto mb-4" />
                      <p className="font-bold text-foreground mb-1">No colleges found in {selectedCity}</p>
                      <p className="text-sm text-muted-foreground">Try selecting a different city or browse all colleges</p>
                      <Link href="/" className="btn-primary mt-4 inline-flex">Browse All Colleges</Link>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-20 bg-card rounded-2xl border border-border">
                  <MapPin size={56} className="text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">Select a City to Explore</h3>
                  <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                    Choose a city from the left panel to see all colleges available in that location.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function CitySearchPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-muted-foreground">Loading...</div></div>}>
      <CitySearchContent />
    </Suspense>
  );
}
